import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { LogIn, AlertCircle } from "lucide-react";
import { login, isAuthenticated, formatApiErrorDetail } from "@/lib/auth";

export default function AdminLogin() {
    const nav = useNavigate();
    const { state: locationState } = useLocation();
    const [form, setForm] = useState({ email: "", password: "" });
    const [status, setStatus] = useState({ loading: false, error: null });

    useEffect(() => {
        if (isAuthenticated()) {
            const target = locationState?.from?.pathname || "/admin/blog";
            nav(target, { replace: true });
        }
    }, []); // eslint-disable-line

    async function onSubmit(e) {
        e.preventDefault();
        setStatus({ loading: true, error: null });
        try {
            await login(form.email, form.password);
            const target = locationState?.from?.pathname || "/admin/blog";
            nav(target, { replace: true });
        } catch (err) {
            setStatus({ loading: false, error: formatApiErrorDetail(err.response?.data?.detail) });
        }
    }

    return (
        <div className="min-h-screen bg-ink text-white flex items-center justify-center px-4">
            <div className="w-full max-w-md" data-testid="admin-login-card">
                <Link to="/" className="flex items-center gap-3 mb-12 text-white/60 hover:text-white">
                    <img src="https://customer-assets.emergentagent.com/job_zealous-goodall-9/artifacts/o7ztlruv_7med.png" alt="7MED" className="h-10 w-auto object-contain" />
                </Link>
                <div className="label-eyebrow !text-brand">Backoffice</div>
                <h1 className="mt-4 font-heading font-black text-5xl tracking-tighter leading-tight">Accesso<br />amministratore</h1>
                <p className="mt-6 text-sm text-white/60">Solo personale autorizzato. Per accedere usa le credenziali fornite.</p>

                <form onSubmit={onSubmit} className="mt-12 space-y-6" data-testid="admin-login-form">
                    <Field label="Email">
                        <input
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                            placeholder="admin@7med.ch"
                            className="swiss-dark-input"
                            data-testid="admin-login-email"
                        />
                    </Field>
                    <Field label="Password">
                        <input
                            type="password"
                            required
                            value={form.password}
                            onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
                            placeholder="••••••••"
                            className="swiss-dark-input"
                            data-testid="admin-login-password"
                        />
                    </Field>
                    {status.error && (
                        <div className="flex items-start gap-3 p-3 bg-brand/20 border border-brand text-sm" data-testid="admin-login-error">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div>{status.error}</div>
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={status.loading}
                        className="w-full btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="admin-login-submit"
                    >
                        {status.loading ? "Accesso..." : "Accedi"} <LogIn className="w-4 h-4" />
                    </button>
                </form>

                <div className="mt-12 text-xs text-white/40 text-center">
                    <Link to="/" className="hover:text-brand">← Torna al sito</Link>
                </div>
            </div>

            <style>{`
                .swiss-dark-input {
                    width: 100%; background: transparent; border: 0;
                    border-bottom: 1px solid rgba(255,255,255,0.25);
                    padding: 12px 0; font-size: 15px; color: #ffffff;
                    outline: none; transition: border-color 0.2s;
                    font-family: "IBM Plex Sans", sans-serif;
                }
                .swiss-dark-input:focus { border-bottom-color: #FF3B30; }
                .swiss-dark-input::placeholder { color: rgba(255,255,255,0.3); }
            `}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-white/50">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}
